// US storefront only. OneTrust supplies effective groups at startup and explicit choices separately.
(() => {
  if (window.AarkeUSConsent) return;

  const outboxKey = 'aarke.us.pending-consent.v1';
  const activePurposes = new Set();
  let current = null;
  let lastGroups;
  let startupTimer;
  let startupChecks = 0;
  let subscribedTo;
  let pending;
  let writing = false;
  let attempts = 0;
  let retryTimer;
  let reloadTimer;
  let featureVersion = 0;
  let interacted = false;

  function decision(detail) {
    const source = Array.isArray(detail) ? detail : window.OnetrustActiveGroups;
    if (!Array.isArray(source) && typeof source !== 'string') return null;
    const groups = new Set(Array.isArray(source) ? source : source.split(',').filter(Boolean));
    const configured = window.OneTrust?.GetDomainData?.()?.Groups;
    // Aarke always includes Necessary. An incomplete SDK state is not a visitor rejection.
    if (!groups.has('C0001') || !Array.isArray(configured) || !configured.length) return null;
    const choice = {
      analytics: groups.has('C0002'),
      marketing: groups.has('C0004')
    };
    // The US rules make Functional mandatory; the Global Audience rule makes it optional
    // and has no sale/sharing control. Only send choices the active rule actually offers.
    const functional = configured.find(group => group.CustomGroupId === 'C0003');
    if (functional && functional.Status !== 'always active') choice.preferences = groups.has('C0003');
    if (navigator.globalPrivacyControl === true || configured.some(group => group.CustomGroupId === 'SSPD_BG')) {
      choice.sale_of_data = groups.has('SSPD_BG') && navigator.globalPrivacyControl !== true;
    }
    return choice;
  }

  function allows(purpose) {
    if (!current) return false;
    const ads = current.marketing && current.sale_of_data !== false;
    if (purpose === 'analytics') return current.analytics;
    if (purpose === 'marketing') return ads;
    // This single container also starts advertising tags on page load.
    return purpose === 'mixed' && current.analytics && ads;
  }
  window.AarkeUSConsent = { allows };

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  function googleConsent(command) {
    const ads = allows('marketing') ? 'granted' : 'denied';
    window.gtag('consent', command, {
      analytics_storage: allows('analytics') ? 'granted' : 'denied',
      ad_storage: ads, ad_user_data: ads, ad_personalization: ads
    });
  }
  googleConsent('default');

  function activateTemplates() {
    if ([...activePurposes].some(purpose => !allows(purpose))) return;
    for (const template of document.querySelectorAll('template[data-aarke-consent]')) {
      const purpose = template.dataset.aarkeConsent;
      if (!allows(purpose)) continue;
      activePurposes.add(purpose);
      // Import the inert template into this document. Its scripts run once, in source order.
      const content = document.importNode(template.content, true);
      template.replaceWith(content);
    }
  }

  function matches(a, b) {
    return a && b && a.analytics === b.analytics && a.marketing === b.marketing &&
      a.sale_of_data === b.sale_of_data && a.preferences === b.preferences;
  }

  function reloadIfRevoked() {
    if ([...activePurposes].some(purpose => !allows(purpose))) window.location.reload();
  }

  function refresh(detail) {
    const next = decision(detail);
    if ((!next && !current) || matches(next, current)) return;
    current = next;
    googleConsent('update');
    clearTimeout(reloadTimer);
    if ([...activePurposes].some(purpose => !allows(purpose))) {
      // Already running vendor code cannot be unloaded. Give Shopify time to persist the withdrawal.
      reloadTimer = setTimeout(reloadIfRevoked, 1500);
    } else {
      activateTemplates();
    }
  }

  function persistPending(value) {
    // Only an actual callback can create an outbox entry. Recover a navigation before Shopify's reply.
    try {
      if (value) sessionStorage.setItem(outboxKey, JSON.stringify({ value, at: Date.now() }));
      else sessionStorage.removeItem(outboxKey);
    } catch { /* Storage is optional; the current page can still deliver the choice. */ }
  }

  function recoverPending() {
    if (pending || !current || !window.OneTrust?.IsAlertBoxClosedAndValid?.()) return;
    let saved;
    try { saved = JSON.parse(sessionStorage.getItem(outboxKey)); } catch { return; }
    if (!saved) return;
    if (Date.now() - saved.at > 300000 || !matches(saved.value, current)) {
      persistPending(null);
      return;
    }
    pending = current;
    flush();
  }

  function reviewMissingRejection(remaining = 5) {
    // Older versions lost this explicit click. Ask the visitor to reconfirm it rather than
    // registering a new Shopify choice from regional defaults or a page view.
    if (interacted || pending || !current || current.analytics || current.marketing ||
        current.sale_of_data !== false || !window.OneTrust?.IsAlertBoxClosedAndValid?.()) return;
    const cookie = document.cookie.split('; ').find(entry => entry.startsWith('OptanonConsent='));
    const fields = new URLSearchParams(cookie?.slice('OptanonConsent='.length) || '');
    const saved = (fields.get('groups') || '').split(',');
    if (!(Number(fields.get('interactionCount')) > 0) ||
        !['C0002:0', 'C0004:0', 'SSPD_BG:0'].every(group => saved.includes(group))) return;
    const stored = window.Shopify?.customerPrivacy?.currentVisitorConsent?.();
    if (!stored) {
      if (remaining) setTimeout(() => reviewMissingRejection(remaining - 1), 400);
      return;
    }
    if (['analytics', 'marketing', 'sale_of_data'].every(key => stored[key] === 'no')) return;
    try {
      if (sessionStorage.getItem('aarke.us.reviewed-rejection')) return;
      sessionStorage.setItem('aarke.us.reviewed-rejection', '1');
    } catch { return; }
    const panel = document.getElementById('onetrust-pc-sdk');
    if (!panel?.getClientRects().length || getComputedStyle(panel).visibility === 'hidden') {
      window.OneTrust.ToggleInfoDisplay();
    }
  }

  function retry() {
    if (retryTimer || !pending) return;
    if (attempts >= 6) {
      console.warn('[Aarke consent] Shopify has not acknowledged the visitor choice.');
      return;
    }
    retryTimer = setTimeout(() => { retryTimer = null; flush(); }, 150 * 2 ** attempts++);
  }

  function finish(sent) {
    if (matches(pending, sent)) {
      pending = null;
      persistPending(null);
      // Let OneTrust finish its own callback before replacing the document.
      if ([...activePurposes].some(purpose => !allows(purpose))) {
        clearTimeout(reloadTimer);
        reloadTimer = setTimeout(reloadIfRevoked, 0);
      }
    }
    if (pending) flush();
  }

  function flush() {
    if (!pending || writing) return;
    const shopify = window.Shopify;
    const api = shopify?.customerPrivacy;
    if (!api?.setTrackingConsent) {
      // This store also loads 0.1. Reuse the live instance, regardless of which version arrived first.
      const versions = ['0.1', '0.2'];
      if (shopify?.loadFeatures && featureVersion < versions.length) {
        try {
          shopify.loadFeatures([{ name: 'consent-tracking-api', version: versions[featureVersion++] }],
            error => { if (!error && shopify.customerPrivacy?.setTrackingConsent) flush(); });
        } catch { /* The loader can still be initializing. Retry against the public API. */ }
      }
      retry();
      return;
    }
    const sent = pending;
    try {
      const stored = api.currentVisitorConsent?.();
      if (stored && Object.keys(sent).every(k => stored[k] === (sent[k] ? 'yes' : 'no'))) {
        finish(sent);
        return;
      }
      writing = true;
      api.setTrackingConsent(sent, error => {
        writing = false;
        if (error) retry();
        else finish(sent);
      });
    } catch { writing = false; retry(); }
  }

  function onChoice(event) {
    const choice = decision(event?.detail);
    if (!choice || !window.OneTrust.IsAlertBoxClosedAndValid()) return;
    interacted = true;
    lastGroups = event?.detail ?? window.OnetrustActiveGroups;
    refresh(event?.detail);
    pending = choice;
    persistPending(choice);
    attempts = 0;
    clearTimeout(retryTimer);
    retryTimer = null;
    flush();
  }

  function register() {
    const cmp = window.OneTrust;
    if (!cmp?.OnConsentChanged) return;
    if (subscribedTo !== cmp) {
      cmp.OnConsentChanged(onChoice);
      subscribedTo = cmp;
    }
    for (const button of document.querySelectorAll('[data-aarke-cookie-settings]')) button.disabled = false;
  }
  function syncFromCMP() {
    register();
    refresh(lastGroups ?? window.OnetrustActiveGroups);
    if (!current && lastGroups && !startupTimer && startupChecks++ < 30) {
      // On a return visit the groups event can precede the SDK's public rule data.
      startupTimer = setTimeout(() => { startupTimer = null; syncFromCMP(); }, 250);
    }
    recoverPending();
    if (current && !interacted) setTimeout(reviewMissingRejection, 0);
  }
  window.OptanonWrapper = syncFromCMP;
  window.addEventListener('OneTrustGroupsUpdated', event => {
    lastGroups = event.detail;
    syncFromCMP();
  });
  document.addEventListener('DOMContentLoaded', () => { syncFromCMP(); activateTemplates(); });
  document.addEventListener('click', event => {
    if (event.target.closest?.('[data-aarke-cookie-settings]')) window.OneTrust?.ToggleInfoDisplay?.();
  });
  syncFromCMP();
})();
